import type {
  QueueSummary,
  QueueTreeNode,
  QueueTreeResponse,
} from "../../../experience-shell/queues/types";

/**
 * Flat list of every active, workable queue in the tree. Personal and
 * inactive queues are dropped: they cannot receive a routed work item.
 */
export function flattenWorkableQueues(roots: readonly QueueTreeNode[]): QueueSummary[] {
  const out: QueueSummary[] = [];
  const visit = (node: QueueTreeNode): void => {
    if (node.queue.isWorkable && node.queue.status === "active") {
      out.push(node.queue);
    }
    for (const child of node.children) visit(child);
  };
  for (const root of roots) visit(root);
  return out;
}

/**
 * Workable queues that match `filter` against the name and ancestor path,
 * minus `excludeIds`, capped at `limit`.
 */
export function filterQueueCandidates(
  tree: QueueTreeResponse | undefined,
  filter: string,
  excludeIds: readonly string[] = [],
  limit = 20,
): QueueSummary[] {
  if (tree === undefined) return [];
  const excluded = new Set(excludeIds);
  const needle = filter.trim().toLowerCase();
  return flattenWorkableQueues(tree.roots)
    .filter((q) => !excluded.has(q.id))
    .filter((q) => {
      if (needle.length === 0) return true;
      return [q.name, ...q.ancestorPath].join(" / ").toLowerCase().includes(needle);
    })
    .slice(0, limit);
}
